import { Box, Icon, Stack, VStack } from "@chakra-ui/react";
import { FiTrendingUp } from "react-icons/fi";
import dayjs from "dayjs";
import type { DisplayMarketingCampaign } from "@common/interfaces/KYDMarketing";
import { CampaignStatus } from "common/interfaces/KYDMarketing";
import StatCard from "../../Components/StatCard";
import Heading from "./components/Heading";
import Headline from "./components/Headline";
import { KYDAdminCampaign } from "@common/interfaces/KYDEvent";
import { getMetric } from "./adSetCardUtils";

/** Campaign is "launched" when it has been published to Meta and is or was running (ACTIVE, PAUSED, or ARCHIVED). */
function isCampaignLaunched(campaign: DisplayMarketingCampaign): boolean {
  const s = campaign.status;
  return (
    s === CampaignStatus.ACTIVE ||
    s === CampaignStatus.PAUSED ||
    s === CampaignStatus.ARCHIVED ||
    s === CampaignStatus.COMPLETED
  );
}

/**
 * Format schedule range with space-saving heuristics:
 * - Same day: show date once + time range (e.g. "Feb 19, 9:00 AM – 5:00 PM").
 * - Same month: show month once (e.g. "Feb 15 – 20, 2025").
 * - Same year, different months: "Feb 15 – Mar 20, 2025".
 * - Different years: "Feb 15, 2024 – Mar 20, 2025".
 */
function getScheduleOrDaysLeft(campaign: DisplayMarketingCampaign): string {
  if (!campaign.schedule) return "—";
  const minStart = dayjs(campaign.schedule.start_time);
  const maxEnd = dayjs(campaign.schedule.stop_time);
  if (!maxEnd.isValid()) return "—";
  if (!minStart.isValid() || !minStart.isBefore(maxEnd)) {
    return maxEnd.format("MMM D, YYYY");
  }

  const sameDay = minStart.isSame(maxEnd, "day");
  const sameMonth = minStart.isSame(maxEnd, "month");
  const sameYear = minStart.isSame(maxEnd, "year");

  if (sameDay) {
    return `${minStart.format("MMM D, h:mm A")} – ${maxEnd.format("h:mm A")}`;
  }
  if (sameMonth && sameYear) {
    return `${minStart.format("MMM D")} – ${maxEnd.format("D, YYYY")}`;
  }
  if (sameYear) {
    return `${minStart.format("MMM D")} – ${maxEnd.format("MMM D, YYYY")}`;
  }
  return `${minStart.format("MMM D, YYYY")} – ${maxEnd.format("MMM D, YYYY")}`;
}

export default function CampaignMetricsCard({
  campaign,
}: {
  campaign: DisplayMarketingCampaign;
}) {
  const metrics = campaign.metrics as KYDAdminCampaign;
  const highestPerforming = getMetric(metrics, "highest_performing_adset_name");
  const showHighestPerforming = isCampaignLaunched(campaign);

  return (
    <VStack w="full" alignItems="flex-start" spacing={4} p={0}>
      <Headline
        title="Campaign Metrics"
        icon={<Icon as={FiTrendingUp} color="#A7A7A7" />}
      />
      <VStack w="full" gap={2} alignItems="flex-start">
        <Heading variant="subheadline">Engagement</Heading>
        <Stack
          w="100%"
          spacing={4}
          direction={{ base: "column", sm: "row" }}
          flexWrap="wrap"
        >
          <Box
            flex={{ base: "1 1 100%", sm: "1" }}
            minW={{ base: "100%", sm: "120px" }}
          >
            <StatCard
              title="Seen"
              value={getMetric(metrics, "display_impressions")}
              minH={{ base: "68px", md: "80px" }}
            />
          </Box>
          <Box
            flex={{ base: "1 1 100%", sm: "1" }}
            minW={{ base: "100%", sm: "120px" }}
          >
            <StatCard
              title="Clicked"
              value={getMetric(metrics, "display_clicks")}
              minH={{ base: "68px", md: "80px" }}
            />
          </Box>
          <Box
            flex={{ base: "1 1 100%", sm: "1" }}
            minW={{ base: "100%", sm: "120px" }}
          >
            <StatCard
              title="Click Through Rate"
              value={getMetric(metrics, "display_ctr")}
              minH={{ base: "68px", md: "80px" }}
            />
          </Box>
          <Box
            flex={{ base: "1 1 100%", sm: "1" }}
            minW={{ base: "100%", sm: "120px" }}
          >
            <StatCard
              title="Cost per click"
              value={getMetric(metrics, "display_cpc")}
              minH={{ base: "68px", md: "80px" }}
            />
          </Box>
          <Box
            flex={{ base: "1 1 100%", sm: "1" }}
            minW={{ base: "100%", sm: "120px" }}
          >
            <StatCard
              title="Cost per 1K impressions"
              value={getMetric(metrics, "display_cpm")}
              minH={{ base: "68px", md: "80px" }}
            />
          </Box>
          {showHighestPerforming && (
            <Box
              flex={{ base: "1 1 100%", sm: "1" }}
              minW={{ base: "100%", sm: "120px" }}
            >
              <StatCard
                title="Best Performing"
                value={highestPerforming}
                textProps={{ fontSize: "sm", noOfLines: 1 }}
                minH={{ base: "68px", md: "80px" }}
              />
            </Box>
          )}
        </Stack>
      </VStack>
      <VStack w="full" gap={2} alignItems="flex-start">
        <Heading variant="subheadline">Budget & Schedule</Heading>
        <Stack
          w="100%"
          spacing={4}
          direction={{ base: "column", sm: "row" }}
          flexWrap="wrap"
        >
          <Box
            flex={{ base: "1 1 100%", sm: "1" }}
            minW={{ base: "100%", sm: "120px" }}
          >
            <StatCard
              title="Budget Remaining"
              value={getMetric(metrics, "display_budget_remaining")}
              minH={{ base: "68px", md: "80px" }}
            />
          </Box>
          <Box
            flex={{ base: "1 1 100%", sm: "1" }}
            minW={{ base: "100%", sm: "120px" }}
          >
            <StatCard
              title="Schedule"
              textProps={{ fontSize: "sm", noOfLines: 1 }}
              value={getScheduleOrDaysLeft(campaign)}
              minH={{ base: "68px", md: "80px" }}
            />
          </Box>
        </Stack>
      </VStack>
    </VStack>
  );
}
